import queryKeys from '@/constants/queryKeys';
import { getAdminFeatureFlags, TFeatureFlagKey, updateFeatureFlags } from '@/services/api/requests/featureFlags';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';

// Super-admin product feature flags: read the list + toggle a single flag. The list
// is refreshed from the server response after a successful update.
const useAdminFeatureFlags = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: flags = [], isLoading, isError, refetch } = useQuery({
    queryKey: [queryKeys.featureFlags.admin],
    queryFn: getAdminFeatureFlags,
  });

  const { mutate: setFlag, isPending: isUpdating } = useMutation({
    mutationFn: ({ key, enabled }: { key: TFeatureFlagKey; enabled: boolean }) => updateFeatureFlags([{ key, enabled }]),
    onSuccess: (updated) => {
      queryClient.setQueryData([queryKeys.featureFlags.admin], updated);
      toast({ status: 'success', title: 'Feature flag updated', duration: 2500, isClosable: true });
    },
    onError: () => {
      toast({
        status: 'error',
        title: 'Couldn’t update the feature flag',
        description: 'You may be viewing an organization in read-only mode. Clear it and try again.',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return { flags, isLoading, isError, refetch, setFlag, isUpdating };
};

export default useAdminFeatureFlags;
